import React, { useMemo, useState } from 'react'
import moment from 'moment-timezone'
import {
  ArrowsDownUpIcon,
  PencilSimpleIcon,
  BuildingsIcon,
} from '@phosphor-icons/react'
import { Client } from '../../types/Client'

export type ClientStatus = 'Active' | 'Inactive'

export const getClientStatus = (client: Pick<ClientRow, 'activeEngagements'>): ClientStatus =>
  client.activeEngagements > 0 ? 'Active' : 'Inactive'

export type ClientRow = Client & {
  activeEngagements: number
  teamsCount: number
}

type SortColumn = 'name' | 'created_at' | 'activeEngagements' | 'teamsCount'

interface Props {
  clients: ClientRow[]
  onRowClick: (client: Client) => void
  onEdit: (client: Client) => void
}

const ClientsTable = ({ clients, onRowClick, onEdit }: Props) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const sortedClients = useMemo(() => {
    const sorted = [...clients].sort((a, b) => {
      let comparison = 0
      if (sortColumn === 'name') {
        comparison = a.name.localeCompare(b.name)
      } else if (sortColumn === 'created_at') {
        comparison = moment(a.created_at).unix() - moment(b.created_at).unix()
      } else if (sortColumn === 'activeEngagements') {
        comparison = a.activeEngagements - b.activeEngagements
      } else if (sortColumn === 'teamsCount') {
        comparison = a.teamsCount - b.teamsCount
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })
    return sorted
  }, [clients, sortColumn, sortOrder])

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  if (clients.length === 0) {
    return (
      <div className="management-clients__empty">
        <div className="management-clients__empty-icon">
          <BuildingsIcon size={48} weight="regular" color="#717182" />
        </div>
        <p className="management-clients__empty-label">
          No clients found. Try adjusting your search or filters.
        </p>
      </div>
    )
  }

  return (
    <div className="management-clients__table-card">
      <table className="management-clients__table">
        <thead className="management-clients__table-head">
          <tr>
            <th
              className="management-clients__table-th"
              style={{ width: '30%' }}
            >
              <button
                type="button"
                className="management-clients__sort-btn"
                onClick={() => handleSort('name')}
              >
                Client Name
                <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
              </button>
            </th>
            <th
              className="management-clients__table-th"
              style={{ width: '30%' }}
            >
              <button
                type="button"
                className="management-clients__sort-btn"
                onClick={() => handleSort('activeEngagements')}
              >
                Active Engagements
                <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
              </button>
            </th>
            <th
              className="management-clients__table-th"
              style={{ width: '30%' }}
            >
              <button
                type="button"
                className="management-clients__sort-btn"
                onClick={() => handleSort('created_at')}
              >
                Created On
                <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
              </button>
            </th>
            <th className="management-clients__table-th management-clients__table-th--actions" />
          </tr>
        </thead>
        <tbody>
          {sortedClients.map(client => {
            return (
              <tr
                key={client.id}
                className="management-clients__table-row"
                onClick={() => onRowClick(client)}
              >
                <td className="management-clients__table-td">
                  <button
                    type="button"
                    className="management-clients__name-btn"
                    onClick={e => {
                      e.stopPropagation()
                      onRowClick(client)
                    }}
                  >
                    {client.name}
                  </button>
                </td>
                <td className="management-clients__table-td">
                  {client.activeEngagements} active
                </td>
                <td className="management-clients__table-td">
                  {moment(client.created_at).format('MM/DD/YYYY')}
                </td>
                <td className="management-clients__table-td management-clients__table-td--actions">
                  <button
                    type="button"
                    className="management-clients__edit-btn"
                    aria-label={`Edit ${client.name}`}
                    onClick={e => {
                      e.stopPropagation()
                      onEdit(client)
                    }}
                  >
                    <PencilSimpleIcon size={18} weight="regular" />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default ClientsTable
